import { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsLeftRight,
  faBolt,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import PlaidIntegrationApiClient from "@/api/plaidIntegrationApiClient";
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from "react-plaid-link";
import "./PlaidIdentityVerification.scss";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import plaidLogo from "@/assets/plaid-logo.png";
import bankLogo from "@/assets/bank-logo.svg";
import { PlaidTestControls, PlaidTestState } from "./PlaidTestControls";
import { IdentityVerificationStatus } from "@/types/ProcessPlaidIdentityVerification";
import { getStatusMessage } from "@/config/identityVerificationStatusMessages";

export interface PlaidIdentityVerificationProps {
  financialApplicationId: string;
}

export interface PlaidIdentityVerificationRef {
  hasRunIdvCheck: () => Promise<boolean>;
}

export const PlaidIdentityVerification = forwardRef<
  PlaidIdentityVerificationRef,
  PlaidIdentityVerificationProps
>(({ financialApplicationId }, ref) => {
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const plaidIntegrationApiClient = useAuthenticatedApiClient(PlaidIntegrationApiClient);
  const [isLoading, setIsLoading] = useState(false);
  const [hasStartedSession, setHasStartedSession] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<IdentityVerificationStatus>(
    IdentityVerificationStatus.Undefined
  );
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const initiateVerification = async () => {
    setIsLoading(true);
    setHasStartedSession(true); // Mark that we've started the session
    try {
      const financialApplication =
        await financialApplicationApiClient.getFinancialApplication(financialApplicationId);
      const response = await plaidIntegrationApiClient.getLinkToken({
        userId: financialApplication.personInformation.idvUserId,
      });
      setLinkToken(response.linkToken);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to get Plaid link token:", error);
      setIsLoading(false);
      setVerificationStatus(IdentityVerificationStatus.Failed);
    }
  };

  // Handle test state changes
  const handleTestStateChange = (newState: PlaidTestState) => {
    if (newState === "success") {
      setVerificationStatus(IdentityVerificationStatus.Success);
    } else if (newState === "failed") {
      setVerificationStatus(IdentityVerificationStatus.Failed);
    } else if (newState === "loading") {
      setIsLoading(true);
      setHasStartedSession(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    } else {
      setVerificationStatus(IdentityVerificationStatus.Undefined);
      setHasStartedSession(false); // Reset session state
      setIsLoading(false);
      setLinkToken(null);
    }
  };

  const onSuccess = useCallback(
    async (_publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      setIsLoading(true);

      try {
        const response = await financialApplicationApiClient.processPlaidIdentityVerification(
          financialApplicationId,
          {
            sessionId: metadata.link_session_id,
          }
        );

        setIsLoading(false);

        if (response.isSuccess) {
          setVerificationStatus(response.status);
        } else {
          console.error("Plaid identity verification failed:", response.errorMessage);
          setVerificationStatus(IdentityVerificationStatus.Failed);
        }
      } catch (error) {
        console.error("Plaid identity verification failed:", error);
        setIsLoading(false);
        setVerificationStatus(IdentityVerificationStatus.Failed);
      }
    },
    [financialApplicationId, financialApplicationApiClient]
  );

  const onExit = useCallback((err: any, _metadata: any) => {
    if (err) {
      console.error('Plaid Link error:', err);
      setVerificationStatus(IdentityVerificationStatus.Canceled);
    }
    // Reset states to allow retry
    setIsLoading(false);
    setHasStartedSession(false);
    setVerificationStatus(IdentityVerificationStatus.Undefined);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Expose submit function to parent
  useImperativeHandle(ref, () => ({
    hasRunIdvCheck: async () => {
      // Return true for completed states (success, failed, expired, canceled, pending review)
      if ([IdentityVerificationStatus.Success, IdentityVerificationStatus.Failed, IdentityVerificationStatus.Expired, IdentityVerificationStatus.Canceled, IdentityVerificationStatus.PendingReview].includes(verificationStatus)) {
        return true;
      }

      // Return false if currently loading
      if (isLoading) {
        return false;
      }

      // If session hasn't been started, initiate verification
      if (!hasStartedSession) {
        await initiateVerification();
        return false; // Return false while verification is in progress
      }

      // If session has been started but no final status yet, return false
      return false;
    },
  }));

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  // Create a single instance of the test controls
  const testControls = <PlaidTestControls onStateChange={handleTestStateChange} />;

  // Get status message based on current status
  const statusInfo = getStatusMessage(verificationStatus);

  // Show loading state when session is active and loading, or when session has started but no final status yet
  if (hasStartedSession && (isLoading || verificationStatus === IdentityVerificationStatus.Undefined)) {
    return (
      <section className="plaid-identity-verification step-section">
        {testControls}
        <div className="plaid-identity-verification__loading">
          <div className="plaid-identity-verification__loading-text">
            <h3>Verifying Your Identity</h3>
            <p>Please wait while we securely verify your identity through Plaid...</p>
          </div>
        </div>
      </section>
    );
  }

  // Show status-specific UI only after onSuccess has been called and we have a final status
  if (hasStartedSession && verificationStatus !== IdentityVerificationStatus.Undefined) {
    const isSuccess = verificationStatus === IdentityVerificationStatus.Success;

    return (
      <section className="plaid-identity-verification step-section">
        {testControls}
        <div className={`plaid-identity-verification__status plaid-identity-verification__status--${isSuccess ? 'success' : 'warning'}`}>
          <div className="plaid-identity-verification__status-header">
            <h3 className="plaid-identity-verification__status-title">
              {statusInfo.title}
            </h3>
            {isSuccess && <div className="plaid-identity-verification__status-icon">✓</div>}
          </div>
          <p className="plaid-identity-verification__status-message">
            {statusInfo.message}
          </p>
        </div>
      </section>
    );
  }

  return (
    <div className="plaid-identity-verification__content">
      {testControls}
      <div className="plaid-identity-verification__integration-indicator">
        <div className="plaid-identity-verification__integration-logo">
          <img
            src={plaidLogo}
            alt="Plaid"
            className="plaid-identity-verification__plaid-logo-img"
          />
        </div>
        <div className="plaid-identity-verification__integration-icon">
          <FontAwesomeIcon
            icon={faArrowsLeftRight}
            className="plaid-identity-verification__integration-symbol"
          />
        </div>
        <div className="plaid-identity-verification__integration-logo">
          <img
            src={bankLogo}
            alt="Client"
            className="plaid-identity-verification__client-logo-img"
          />
        </div>
      </div>
      <div className="plaid-identity-verification__info-list plaid-identity-verification__info-list--centered">
        <div className="plaid-identity-verification__info-item plaid-identity-verification__info-item--centered">
          <FontAwesomeIcon
            icon={faBolt}
            className="plaid-identity-verification__info-icon plaid-identity-verification__info-icon--centered"
          />
          <div className="plaid-identity-verification__info-content plaid-identity-verification__info-content--centered">
            <h4>Fast and secure</h4>
            <p>Verification is encrypted and usually takes less than two minutes.</p>
          </div>
        </div>
        <div className="plaid-identity-verification__info-item plaid-identity-verification__info-item--centered">
          <FontAwesomeIcon
            icon={faLock}
            className="plaid-identity-verification__info-icon plaid-identity-verification__info-icon--centered"
          />
          <div className="plaid-identity-verification__info-content plaid-identity-verification__info-content--centered">
            <h4>How we verify you</h4>
            <p>For details related to how Plaid handles your data, see their Privacy Statement.</p>
          </div>
        </div>
      </div>
    </div>
  );
});
